/**
 * Gift cards del carrito (ENA-1638, contrato en ENA-1730 y en el comentario de backend del 7/10):
 * aplicar y quitar devuelven el carrito; las aplicadas salen de `discountSummary.giftCards`.
 */
import { PaymentRequestError } from '../domain/errors';
import type { GiftCardCart, GiftCardItem } from '../domain/models';
import { translate } from '../i18n';

export const MAX_GIFT_CARDS = 5;

/** Provider VTEX para tarjeta de regalo OH (único tipo soportado en headless). */
export const OH_GIFTCARD_PROVIDER = 'ohgiftcard';

/** Gift cards en uso del carrito, de todos los grupos (tarjeta de regalo y compromiso ticket más bajo). */
export const giftCardsFromCart = (cart: GiftCardCart): GiftCardItem[] =>
  (cart.discountSummary?.giftCards?.groups ?? []).flatMap((group) => group.items ?? []);

const normalizeRedemptionCode = (redemptionCode: string): string =>
  redemptionCode.replace(/[\s-]/g, '').toUpperCase();

/** VTEX puede responder 200 sin aplicar la tarjeta (p. ej. `invalidGiftCard` en messages del order form). */
export const isGiftCardAppliedOnCart = (cart: GiftCardCart, redemptionCode: string): boolean => {
  const expected = normalizeRedemptionCode(redemptionCode);
  return giftCardsFromCart(cart).some(
    (card) => normalizeRedemptionCode(card.redemptionCode) === expected,
  );
};

export const assertGiftCardApplied = (cart: GiftCardCart, redemptionCode: string): void => {
  if (!isGiftCardAppliedOnCart(cart, redemptionCode)) {
    throw new PaymentRequestError('Gift card not applied', { failure: 'http', status: 400 });
  }
};

/**
 * Mensaje ante una falla al aplicar o quitar una gift card.
 *
 * TODO(ENA-1638) [consultar a backend]: propagar `messages` de VTEX (p. ej. invalidGiftCard) para textos más finos.
 */
export const giftCardErrorMessage = (error: unknown): string => {
  if (error instanceof PaymentRequestError) {
    if (error.failure === 'http' && (error.status === 400 || error.status === 404)) {
      return translate('paymentsUI.giftCard.errors.invalid');
    }
    if (error.failure === 'timeout' || error.failure === 'network') {
      return translate('paymentsUI.giftCard.errors.timeout');
    }
  }
  return translate('paymentsUI.giftCard.errors.generic');
};
